import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Calendar, MapPin, Ticket, Download, Loader2, AlertCircle, CheckCircle, Music, PartyPopper, Theater, Trophy, UtensilsCrossed, Church, CalendarDays, Clock, RefreshCw, Languages, Sparkles, ImagePlus, Star } from 'lucide-react';
import { api } from '@/lib/api';
import { StatusBadge } from '@/components/admin/StatusBadge';
import { QrDownloadButton } from '@/components/admin/AdminShare';
import { toast } from 'sonner';

const CATEGORY_OPTIONS = [
  { slug: 'concerts', label: 'Conciertos', icon: Music },
  { slug: 'festivals', label: 'Festivales', icon: PartyPopper },
  { slug: 'cultural', label: 'Cultura', icon: Theater },
  { slug: 'sports', label: 'Deportes', icon: Trophy },
  { slug: 'gastronomy', label: 'Gastronomia', icon: UtensilsCrossed },
  { slug: 'traditional', label: 'Tradiciones', icon: Church },
  { slug: 'general', label: 'General', icon: CalendarDays },
];

const AdminEvents = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [importing, setImporting] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [lastImportDate, setLastImportDate] = useState(null);
  const [translatingId, setTranslatingId] = useState(null);
  const [rescrapingId, setRescrapingId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const editFormRef = useRef(null);
  const [formData, setFormData] = useState({
    title_es: '',
    title_en: '',
    title_de: '',
    description_es: '',
    bot_notes: '',
    description_en: '',
    description_de: '',
    event_date: '',
    event_time: '',
    end_date: '',
    venue_name: '',
    venue_address: '',
    latitude: '',
    longitude: '',
    category: 'general',
    image: '',
    ticket_url: '',
    website: '',
    instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    whatsapp: '',
    phone: '',
    featured: false,
    kids_friendly: false,
    outdoor: false,
    traditional: false,
    is_free: false,
    price_info: '',
    source: 'manual',
  });

  useEffect(() => {
    loadEvents();
    // Check last import date from localStorage
    const savedDate = localStorage.getItem('lastEventsImport');
    if (savedDate) {
      setLastImportDate(new Date(savedDate));
    }
  }, []);

  const toggleFeatured = async (event) => {
    try {
      await api.patchEvent(event.id, { featured: !event.featured });
      setEvents(evs => evs.map(e => e.id === event.id ? { ...e, featured: !event.featured } : e));
    } catch { toast.error('No se pudo cambiar el destacado'); }
  };

  const loadEvents = async () => {
    try {
      const res = await api.getEvents({ upcoming_only: false, limit: 200, status: 'all' });
      setEvents(res.data);
    } catch (e) {
      console.error('Error loading events:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      };

      if (editingEvent) {
        await api.updateEvent(editingEvent.id, data);
      } else {
        await api.createEvent(data);
      }
      setShowForm(false);
      setEditingEvent(null);
      resetForm();
      loadEvents();
    } catch (e) {
      console.error('Error saving event:', e);
      alert('Error al guardar el evento');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title_es: event.title_es || '',
      title_en: event.title_en || '',
      title_de: event.title_de || '',
      description_es: event.description_es || '',
      bot_notes: event.bot_notes || '',
      description_en: event.description_en || '',
      description_de: event.description_de || '',
      event_date: event.event_date || '',
      event_time: event.event_time || '',
      end_date: event.end_date || '',
      venue_name: event.venue_name || '',
      venue_address: event.venue_address || '',
      latitude: event.latitude?.toString() || '',
      longitude: event.longitude?.toString() || '',
      category: event.category || 'general',
      image: event.image || '',
      ticket_url: event.ticket_url || '',
      website: event.website || '',
      instagram_url: event.instagram_url || '',
      facebook_url: event.facebook_url || '',
      tiktok_url: event.tiktok_url || '',
      whatsapp: event.whatsapp || '',
      phone: event.phone || '',
      featured: !!event.featured,
      kids_friendly: !!event.kids_friendly,
      outdoor: !!event.outdoor,
      traditional: !!event.traditional,
      is_free: !!event.is_free,
      price_info: event.price_info || '',
      source: event.source || 'manual',
    });
    setShowForm(true);
    // Scroll to form after render
    setTimeout(() => editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await api.deleteEvent(eventId);
      loadEvents();
    } catch (e) {
      console.error('Error deleting event:', e);
      alert('Error al eliminar el evento');
    }
  };

  const handleTranslate = async (eventId) => {
    setTranslatingId(eventId);
    try {
      const res = await api.translateEvent(eventId);
      if (res.data.success) {
        alert(`Evento traducido (${res.data.translated_fields.length} campos)`);
        loadEvents();
      }
    } catch (e) {
      alert('Error al traducir: ' + (e.response?.data?.detail || e.message));
    } finally { setTranslatingId(null); }
  };

  const handleRescrape = async (eventId) => {
    setRescrapingId(eventId);
    try {
      const res = await api.rescrapeEvent(eventId);
      if (res.data.success) {
        alert(`Actualizado: ${res.data.updated_fields.join(', ') || 'sin cambios'}`);
        loadEvents();
      } else {
        alert(res.data.message || 'No se pudo obtener datos');
      }
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setRescrapingId(null); }
  };

  const handleGenerateSummary = async (eventId) => {
    setGeneratingId(eventId);
    try {
      const res = await api.generateEventSummary(eventId);
      if (res.data.success) {
        alert('Resumen IA generado en ES/EN/DE');
        loadEvents();
      }
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setGeneratingId(null); }
  };

  const handleTranslateAll = async () => {
    const untranslated = events.filter(e => !e.title_en || !e.title_de);
    if (untranslated.length === 0) {
      alert('Todos los eventos ya estan traducidos');
      return;
    }
    if (!window.confirm(`¿Traducir ${untranslated.length} eventos sin traducir?`)) return;
    
    setTranslatingId('all');
    try {
      const res = await api.translateAllEvents();
      if (res.data.success) {
        alert(`Traducidos ${res.data.translated} eventos`);
        loadEvents();
      }
    } catch (e) {
      console.error('Batch translation error:', e);
      alert('Error al traducir: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTranslatingId(null);
    }
  };

  // Count untranslated
  const untranslatedCount = events.filter(e => !e.title_en || !e.title_de).length;

  const handleImportAll = async () => {
    setImporting(true);
    setImportResult(null);
    try {
      const res = await api.importAllEvents();
      setImportResult(res.data);
      const now = new Date();
      localStorage.setItem('lastEventsImport', now.toISOString());
      setLastImportDate(now);
      loadEvents();
    } catch (e) {
      console.error('Import error:', e);
      setImportResult({ error: e.response?.data?.detail || 'Error al importar eventos' });
    } finally {
      setImporting(false);
    }
  };

  const handleCleanupPast = async () => {
    if (!window.confirm('¿Eliminar todos los eventos cuya fecha ya ha pasado?')) return;
    setCleaning(true);
    try {
      const res = await api.cleanupPastEvents();
      alert(res.data.message || `Se eliminaron ${res.data.deleted} eventos pasados`);
      loadEvents();
    } catch (e) {
      console.error('Cleanup error:', e);
      alert('Error al limpiar eventos pasados');
    } finally {
      setCleaning(false);
    }
  };

  // Check if should show reminder (more than 7 days since last import)
  const shouldShowReminder = () => {
    if (!lastImportDate) return true;
    const daysSinceImport = Math.floor((new Date() - lastImportDate) / (1000 * 60 * 60 * 24));
    return daysSinceImport >= 7;
  };

  const getDaysSinceImport = () => {
    if (!lastImportDate) return null;
    return Math.floor((new Date() - lastImportDate) / (1000 * 60 * 60 * 24));
  };

  const resetForm = () => {
    setFormData({
      title_es: '',
      title_en: '',
      title_de: '',
      description_es: '',
    bot_notes: '',
      description_en: '',
      description_de: '',
      event_date: '',
      event_time: '',
      end_date: '',
      venue_name: '',
      venue_address: '',
      latitude: '',
      longitude: '',
      category: 'general',
      image: '',
      ticket_url: '',
    website: '',
    instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    whatsapp: '',
    phone: '',
    featured: false,
    kids_friendly: false,
    outdoor: false,
    traditional: false,
    is_free: false,
      price_info: '',
      source: 'manual',
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#38B2AC]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-events">
      {/* Import reminder alert */}
      {shouldShowReminder() && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Clock size={20} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-amber-800 mb-1">
              {lastImportDate 
                ? `Han pasado ${getDaysSinceImport()} dias desde la ultima importacion`
                : 'Nunca has importado eventos de Ticketmaster'}
            </h3>
            <p className="text-sm text-amber-700">
              Te recomendamos importar eventos nuevos regularmente para mantener el calendario actualizado con conciertos y espectaculos.
            </p>
          </div>
          <button
            onClick={handleImportAll}
            disabled={importing}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Importar ahora
          </button>
        </div>
      )}

      {/* Header with actions */}
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <h2 className="text-lg font-semibold text-[#1D1D1F]">
          Eventos ({events.length})
        </h2>
        <div className="flex gap-2">
          {untranslatedCount > 0 && (
            <button
              onClick={handleTranslateAll}
              disabled={translatingId === 'all'}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50"
              data-testid="translate-all-events-btn"
            >
              {translatingId === 'all' ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />}
              Traducir {untranslatedCount}
            </button>
          )}
          <button
            onClick={handleCleanupPast}
            disabled={cleaning}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
            data-testid="cleanup-events-btn"
          >
            {cleaning ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            Limpiar pasados
          </button>
          <button
            onClick={handleImportAll}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
            data-testid="import-events-btn"
          >
            {importing ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Importar Eventos
          </button>
          <button
            onClick={() => { setShowForm(true); setEditingEvent(null); resetForm(); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#38B2AC] text-white rounded-lg text-sm font-medium hover:bg-[#319795]"
            data-testid="add-event-btn"
          >
            <Plus size={16} />
            Nuevo Evento
          </button>
        </div>
      </div>

      {/* Import result message */}
      {importResult && (
        <div className={`p-4 rounded-lg ${importResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
          {importResult.error ? (
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span>{importResult.error}</span>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle size={18} />
                <span className="font-medium">Importados {importResult.total_imported} eventos</span>
              </div>
              {importResult.details && (
                <div className="text-sm text-gray-600 pl-6 grid grid-cols-2 gap-1">
                  <p>Ticketmaster: {importResult.details.ticketmaster?.imported || 0} {importResult.details.ticketmaster?.error && <span className="text-orange-500">(error)</span>}</p>
                  <p>WebTenerife: {importResult.details.webtenerife?.imported || 0} {importResult.details.webtenerife?.error && <span className="text-orange-500">(error)</span>}</p>
                  <p>Santa Cruz: {importResult.details.santacruz?.imported || 0} {importResult.details.santacruz?.error && <span className="text-orange-500">(error)</span>}</p>
                  <p>ICD Cultural: {importResult.details.icdcultural?.imported || 0} {importResult.details.icdcultural?.error && <span className="text-orange-500">(error)</span>}</p>
                  <p>Lucha Canaria: {importResult.details.lucha_canaria?.imported || 0} {importResult.details.lucha_canaria?.error && <span className="text-orange-500">(error)</span>}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Form - only shown when creating new (not editing) */}
      {showForm && !editingEvent && (
        <div ref={editFormRef} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <h3 className="font-semibold text-[#1D1D1F] mb-4">Nuevo Evento</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titulo (ES) *</label>
                <input
                  type="text"
                  value={formData.title_es}
                  onChange={e => setFormData(f => ({ ...f, title_es: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                  data-testid="event-title-es"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titulo (EN)</label>
                <input
                  type="text"
                  value={formData.title_en}
                  onChange={e => setFormData(f => ({ ...f, title_en: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            {/* Date and time */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                <input
                  type="date"
                  value={formData.event_date}
                  onChange={e => setFormData(f => ({ ...f, event_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                  data-testid="event-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                <input
                  type="time"
                  value={formData.event_time}
                  onChange={e => setFormData(f => ({ ...f, event_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={e => setFormData(f => ({ ...f, end_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <select
                  value={formData.category}
                  onChange={e => setFormData(f => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="event-category"
                >
                  {CATEGORY_OPTIONS.map(cat => (
                    <option key={cat.slug} value={cat.slug}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Venue */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lugar</label>
                <input
                  type="text"
                  value={formData.venue_name}
                  onChange={e => setFormData(f => ({ ...f, venue_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Auditorio de Tenerife"
                  data-testid="event-venue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Direccion</label>
                <input
                  type="text"
                  value={formData.venue_address}
                  onChange={e => setFormData(f => ({ ...f, venue_address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Santa Cruz de Tenerife"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion (ES)</label>
              <textarea
                value={formData.description_es}
                onChange={e => setFormData(f => ({ ...f, description_es: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                rows={3}
                data-testid="event-description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas internas para el bot <span className="text-xs font-normal text-gray-400">(no las ve el turista)</span></label>
              <textarea
                value={formData.bot_notes}
                onChange={e => setFormData(f => ({ ...f, bot_notes: e.target.value }))}
                className="w-full px-3 py-2 border border-amber-200 bg-amber-50/50 rounded-lg text-sm"
                rows={2}
                data-testid="event-bot-notes"
              />
            </div>

            {/* Image and tickets */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Imagen</label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={e => setFormData(f => ({ ...f, image: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Entradas</label>
                <input
                  type="url"
                  value={formData.ticket_url}
                  onChange={e => setFormData(f => ({ ...f, ticket_url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="https://..."
                  data-testid="event-ticket-url"
                />
              </div>
            </div>

            {/* Links shown as buttons in the event sheet */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="event-links-fields">
              {[['website', 'Web'], ['instagram_url', 'Instagram URL'], ['facebook_url', 'Facebook URL'], ['tiktok_url', 'TikTok URL'], ['whatsapp', 'WhatsApp (34600000000)'], ['phone', 'Teléfono']].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type="text"
                    value={formData[key]}
                    onChange={e => setFormData(f => ({ ...f, [key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid={`event-${key.replace('_', '-')}`}
                  />
                </div>
              ))}
            </div>

            {/* Flags shown as tags on the public cards */}
            <div className="flex flex-wrap gap-4" data-testid="event-flags">
              {[['featured', 'Destacado (siempre en tarjeta grande)'], ['kids_friendly', 'Para niños'], ['outdoor', 'Al aire libre'], ['traditional', 'Tradicional / canario'], ['is_free', 'Gratis (forzar)']].map(([key, label]) => (
                <label key={key} className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!formData[key]} onChange={e => setFormData(f => ({ ...f, [key]: e.target.checked }))} className="w-4 h-4 accent-[#38B2AC]" data-testid={`event-flag-${key}`} />
                  {label}
                </label>
              ))}
            </div>

            {/* Price info */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Info de precios</label>
              <input
                type="text"
                value={formData.price_info}
                onChange={e => setFormData(f => ({ ...f, price_info: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Desde 25EUR, Entrada gratuita, etc."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="px-4 py-2 bg-[#38B2AC] text-white rounded-lg text-sm font-medium hover:bg-[#319795]"
                data-testid="save-event-btn"
              >
                {editingEvent ? 'Guardar cambios' : 'Crear evento'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingEvent(null); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Events list */}
      <div className="space-y-3">
        {events.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No hay eventos. Crea uno o importa desde las APIs.</p>
        ) : (
          events.map(event => {
            const catOpt = CATEGORY_OPTIONS.find(c => c.slug === event.category);
            const Icon = catOpt?.icon || CalendarDays;
            
            return (
              <React.Fragment key={event.id}>
              <div
                className="flex items-center gap-4 bg-white rounded-xl p-4 border border-gray-100"
                data-testid={`admin-event-${event.id}`}
              >
                {/* Image or icon */}
                <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                  {event.image ? (
                    <img src={event.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon size={24} className="text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      event.source === 'eventbrite' ? 'bg-orange-100 text-orange-700' :
                      event.source === 'ticketmaster' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {event.source}
                    </span>
                    <span className="text-xs text-gray-400">{catOpt?.label || event.category}</span>
                  </div>
                  <h3 className="font-medium text-[#1D1D1F] truncate flex items-center gap-2">
                    <button onClick={(ev) => { ev.stopPropagation(); toggleFeatured(event); }} title={event.featured ? 'Quitar destacado' : 'Destacar'} className={event.featured ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'} data-testid={`event-featured-${event.id}`}>
                      <Star size={16} fill={event.featured ? 'currentColor' : 'none'} />
                    </button>
                    {event.title_es} <StatusBadge item={event} resource="events" onChanged={loadEvents} /></h3>
                  {(!event.title_en || !event.title_de) && (
                    <span className="text-xs text-amber-500 mt-0.5">Sin traducir</span>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(event.event_date)}
                    </span>
                    {event.venue_name && (
                      <span className="flex items-center gap-1 truncate">
                        <MapPin size={12} />
                        {event.venue_name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-1">
                  <button onClick={() => handleRescrape(event.id)} disabled={rescrapingId === event.id}
                    className="p-2 text-gray-500 hover:text-green-500 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                    title="Buscar foto y descripcion de la web" data-testid={`rescrape-event-${event.id}`}>
                    {rescrapingId === event.id ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                  </button>
                  <button onClick={() => handleGenerateSummary(event.id)} disabled={generatingId === event.id}
                    className="p-2 text-gray-500 hover:text-purple-500 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                    title="Generar resumen con IA" data-testid={`ai-summary-event-${event.id}`}>
                    {generatingId === event.id ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                  </button>
                  <button onClick={() => handleTranslate(event.id)} disabled={translatingId === event.id}
                    className="p-2 text-gray-500 hover:text-blue-500 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                    title="Traducir a EN/DE" data-testid={`translate-event-${event.id}`}>
                    {translatingId === event.id ? <Loader2 size={18} className="animate-spin" /> : <Languages size={18} />}
                  </button>
                  <QrDownloadButton kind="e" itemId={event.id} name={event.title_es} />
                  <button onClick={() => handleEdit(event)}
                    className="p-2 text-gray-500 hover:text-[#38B2AC] hover:bg-gray-50 rounded-lg"
                    data-testid={`edit-event-${event.id}`}>
                    <Pencil size={18} />
                  </button>
                  <button onClick={() => handleDelete(event.id)}
                    className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-50 rounded-lg"
                    data-testid={`delete-event-${event.id}`}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              {/* Inline edit form - appears below this event */}
              {showForm && editingEvent?.id === event.id && (
                <div ref={editFormRef} className="bg-gray-50 rounded-xl p-4 border border-gray-200 -mt-1">
                  <h3 className="font-semibold text-[#1D1D1F] mb-4">Editar Evento</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Titulo (ES) *</label>
                        <input type="text" value={formData.title_es} onChange={e => setFormData(f => ({ ...f, title_es: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Titulo (EN)</label>
                        <input type="text" value={formData.title_en} onChange={e => setFormData(f => ({ ...f, title_en: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                        <input type="date" value={formData.event_date} onChange={e => setFormData(f => ({ ...f, event_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                        <input type="time" value={formData.event_time} onChange={e => setFormData(f => ({ ...f, event_time: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
                        <input type="date" value={formData.end_date} onChange={e => setFormData(f => ({ ...f, end_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                        <select value={formData.category} onChange={e => setFormData(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          {CATEGORY_OPTIONS.map(cat => <option key={cat.slug} value={cat.slug}>{cat.label}</option>)}
                        </select></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Lugar</label>
                        <input type="text" value={formData.venue_name} onChange={e => setFormData(f => ({ ...f, venue_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Direccion</label>
                        <input type="text" value={formData.venue_address} onChange={e => setFormData(f => ({ ...f, venue_address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                    </div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Descripcion (ES)</label>
                      <textarea value={formData.description_es} onChange={e => setFormData(f => ({ ...f, description_es: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={3} /></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">URL Imagen</label>
                        <input type="url" value={formData.image} onChange={e => setFormData(f => ({ ...f, image: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">URL Entradas</label>
                        <input type="url" value={formData.ticket_url} onChange={e => setFormData(f => ({ ...f, ticket_url: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button type="submit" className="px-4 py-2 bg-[#38B2AC] text-white rounded-lg text-sm font-medium hover:bg-[#319795]">Guardar cambios</button>
                      <button type="button" onClick={() => { setShowForm(false); setEditingEvent(null); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300">Cancelar</button>
                    </div>
                  </form>
                </div>
              )}
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminEvents;
